import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Divider,
  FormControl,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import CommonSwitchInput from 'components/commonInput/CommonSwitchInput';
import GooglePlacesAutocomplete from 'components/googlePlaces/GooglePlacesAutocomplete';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import { addRegionValidationSchema } from 'utils/CommonValidation';
import {
  currencyOptions,
  sideBarRoutes,
  timezoneOptions,
} from 'utils/StaticData';
import { DEDUCTION_FIELDS } from './constants';

const SectionTitle = ({ children }) => (
  <Text
    fontSize="sm"
    fontWeight="700"
    textTransform="uppercase"
    letterSpacing="0.5px"
    color="gray.500"
    mb={3}
  >
    {children}
  </Text>
);

SectionTitle.propTypes = {
  children: PropTypes.node,
};

const AddRegionModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  adminDetails,
}) => {
  const { textColor, brandStars } = useColorModeVariables();
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    control,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(addRegionValidationSchema),
    defaultValues: {
      regionName: '',
      regionCode: '',
      timezone: null,
      tax: '',
      lostCharge: '',
      profitShare: '',
      settlementDays: '',
      orgName: '',
      orgAddress: '',
      orgCode: '',
      landmark: '',
      eInvoiceFee: '',
      currencyOption: currencyOptions[0],
      defaultIncludePowerbankCost: true,
      defaultIncludeCustomerPurchases: true,
      defaultIncludeTransactionCost: true,
      defaultIncludeEInvoiceFee: false,
    },
  });

  const handleCloseModal = () => {
    reset();
    onClose();
  };

  const handlePlaceSelected = useCallback(
    (placeData) => {
      setValue('regionCode', placeData.place.address_components[0].short_name);
    },
    [setValue],
  );

  const handleFormSubmit = (data) => {
    const combinedAddress = `${data.orgName} - ${data.orgAddress} - ${data.orgCode}`;

    const payload = {
      regionName: data.regionName,
      regionCode: data.regionCode,
      timezone: data.timezone?.id,
      tax: Number(data.tax),
      lostCharge: Number(data.lostCharge),
      profitShare: Number(data.profitShare),
      settlementDays: Number(data.settlementDays),
      orgName: data.orgName,
      orgAddress: data.orgAddress,
      orgCode: data.orgCode,
      address: combinedAddress,
      landmark: data.landmark,
      eInvoiceFee: Number(data.eInvoiceFee),
      defaultIncludePowerbankCost: Boolean(data.defaultIncludePowerbankCost),
      defaultIncludeCustomerPurchases: Boolean(
        data.defaultIncludeCustomerPurchases,
      ),
      defaultIncludeTransactionCost: Boolean(
        data.defaultIncludeTransactionCost,
      ),
      defaultIncludeEInvoiceFee: Boolean(data.defaultIncludeEInvoiceFee),
      ...(data.currencyOption?.code && { currency: data.currencyOption.code }),
      ...(data.currencyOption?.symbol && {
        currencySymbol: data.currencyOption.symbol,
      }),
    };
    onSubmit(payload, reset);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      size="3xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="xl" fontWeight="bold" pb={2}>
          Add New Region
          <Text fontSize="sm" fontWeight="400" color="gray.500" mt={1}>
            Create a region and set its default merchant deductions.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            <FormControl>
              {/* Region information */}
              <SectionTitle>Region Information</SectionTitle>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                <GooglePlacesAutocomplete
                  value={watch('regionName')}
                  onChange={(value) => setValue('regionName', value)}
                  onPlaceSelected={handlePlaceSelected}
                  labelText="Region Name"
                  placeholder="Search for a region..."
                  textColor={textColor}
                  errors={errors?.regionName?.message}
                  isRequired={true}
                />
                <CommonInput
                  type="text"
                  name="regionCode"
                  labelText="Region Code"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Region Code"
                  register={register('regionCode')}
                  errors={errors?.regionCode}
                  isValidate={true}
                  readOnly={true}
                />
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Timezone"
                      name="timezone"
                      type="select"
                      options={timezoneOptions}
                      value={value}
                      onChange={onChange}
                      register={register}
                      isRequired={true}
                      error={errors?.timezone}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                    />
                  )}
                />
                <Controller
                  name="currencyOption"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Currency"
                      name="currencyOption"
                      type="select"
                      options={currencyOptions}
                      value={value}
                      onChange={onChange}
                      register={register}
                      isRequired={false}
                      error={errors?.currencyOption}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.code}
                    />
                  )}
                />
              </SimpleGrid>

              <Divider my={6} />

              {/* Organization details */}
              <SectionTitle>Organization Details</SectionTitle>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                <CommonInput
                  type="text"
                  name="orgName"
                  labelText="Organization Name"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Organization Name"
                  register={register('orgName')}
                  errors={errors?.orgName}
                  isValidate={true}
                />
                <CommonInput
                  type="text"
                  name="orgCode"
                  labelText="Organization Code"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Organization Code"
                  register={register('orgCode')}
                  errors={errors?.orgCode}
                  isValidate={true}
                />
                <CommonInput
                  type="text"
                  name="orgAddress"
                  labelText="Organization Address"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Organization Address"
                  register={register('orgAddress')}
                  errors={errors?.orgAddress}
                  isValidate={true}
                />
                {(adminDetails?.role === sideBarRoutes?.Admin ||
                  adminDetails?.role === sideBarRoutes?.Dealer) && (
                  <CommonInput
                    type="text"
                    name="landmark"
                    labelText="Invoice Generated"
                    textColor={textColor}
                    brandStars={brandStars}
                    placeholder="Invoice Generated"
                    register={register('landmark')}
                    errors={errors?.landmark}
                    isValidate={true}
                  />
                )}
              </SimpleGrid>

              <Divider my={6} />

              {/* Financial settings */}
              <SectionTitle>Financial Settings</SectionTitle>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="20px">
                <CommonInput
                  type="number"
                  name="tax"
                  labelText="Tax"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Tax"
                  register={register('tax')}
                  errors={errors?.tax}
                  isValidate={true}
                  isAddPercentage={true}
                />
                <CommonInput
                  type="number"
                  name="lostCharge"
                  labelText="Lost Charge"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Lost Charge"
                  register={register('lostCharge')}
                  errors={errors?.lostCharge}
                  isValidate={true}
                />
                {adminDetails?.role === sideBarRoutes?.Admin && (
                  <CommonInput
                    type="number"
                    name="profitShare"
                    labelText="Profit Sharing"
                    textColor={textColor}
                    brandStars={brandStars}
                    placeholder="Profit Sharing"
                    register={register('profitShare')}
                    errors={errors?.profitShare}
                    isValidate={true}
                    isAddPercentage={true}
                  />
                )}
                <CommonInput
                  type="number"
                  name="settlementDays"
                  labelText="Settlement Days"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Settlement Days"
                  register={register('settlementDays')}
                  errors={errors?.settlementDays}
                  isValidate={true}
                />
                <CommonInput
                  type="number"
                  name="eInvoiceFee"
                  labelText="E-Invoice Fee"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="E-Invoice Fee"
                  register={register('eInvoiceFee')}
                  errors={errors?.eInvoiceFee}
                  isValidate={true}
                />
              </SimpleGrid>

              <Divider my={6} />

              {/* Default merchant deductions */}
              <SectionTitle>Default Merchant Deductions</SectionTitle>
              <Text fontSize="sm" color="gray.500" mb={3}>
                Applied to new merchants created in this region.
              </Text>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing="12px"
                bg="gray.50"
                p={5}
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.200"
              >
                {DEDUCTION_FIELDS.map((deduction) => (
                  <Box key={deduction.field}>
                    <CommonSwitchInput
                      labelText={deduction.label}
                      name={deduction.field}
                      register={register(deduction.field)}
                      isValidate={false}
                      textColor="gray.700"
                      brandStars={brandStars}
                      control={control}
                      infoText={deduction.infoText}
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </FormControl>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            bg="reviveOrange"
            color="primary"
            _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isLoading}
          >
            Add Region
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

AddRegionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  adminDetails: PropTypes.object,
};

export default AddRegionModal;
